import { useState, useEffect, useCallback } from 'react';
import {
  Image as ImageIcon,
  Search,
  Copy,
  Check,
  Eye,
  X,
  Download,
  Link as LinkIcon,
  Loader2,
} from 'lucide-react';
import {
  getSharedMedia,
  formatFileSize,
} from '../../services/sharedMediaService';
import {
  SHARED_MEDIA_CATEGORIES,
  SHARED_MEDIA_CATEGORY_LABELS,
  type SharedMedia,
  type SharedMediaCategory,
} from '../../types/sharedMedia';
import { logger } from '../../lib/logger';

interface SharedMediaGridProps {
  onSelectMedia?: (media: SharedMedia) => void;
}

export function SharedMediaGrid({ onSelectMedia }: SharedMediaGridProps) {
  const [mediaList, setMediaList] = useState<SharedMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<SharedMediaCategory | ''>('');
  const [selectedMedia, setSelectedMedia] = useState<SharedMedia | null>(null);
  const [copied, setCopied] = useState(false);

  const loadMedia = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters: { search?: string; category?: SharedMediaCategory; is_active?: boolean } = {
        is_active: true, // Users only see active media
      };
      if (search) filters.search = search;
      if (categoryFilter) filters.category = categoryFilter;

      const data = await getSharedMedia(filters);
      setMediaList(data);
    } catch (error) {
      logger.error('Error loading shared media:', error);
    } finally {
      setIsLoading(false);
    }
  }, [search, categoryFilter]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadMedia();
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error('Copy error:', error);
    }
  };

  const handleMediaClick = (media: SharedMedia) => {
    if (onSelectMedia) {
      onSelectMedia(media);
    } else {
      setSelectedMedia(media);
    }
  };

  const filteredMedia = mediaList;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen..."
            className="w-full pl-11 pr-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:border-primary focus:outline-none bg-white dark:bg-slate-800 dark:text-white"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as SharedMediaCategory | '')}
          className="px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 dark:text-white focus:border-primary focus:outline-none"
        >
          <option value="">Alle Kategorien</option>
          {SHARED_MEDIA_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {SHARED_MEDIA_CATEGORY_LABELS[cat]}
            </option>
          ))}
        </select>
      </form>

      {/* Grid */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        {filteredMedia.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
              Keine geteilten Medien
            </h3>
            <p className="text-gray-500 dark:text-slate-400">
              {search || categoryFilter
                ? 'Versuche andere Filterkriterien'
                : 'Es wurden noch keine Medien geteilt'}
            </p>
          </div>
        ) : (
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredMedia.map((media) => (
                <div
                  key={media.id}
                  className="relative group aspect-square rounded-xl overflow-hidden cursor-pointer border-2 border-transparent hover:border-gray-200 dark:hover:border-slate-600 transition-colors"
                  onClick={() => handleMediaClick(media)}
                >
                  <img
                    src={media.url}
                    alt={media.alt_text || media.filename}
                    className="w-full h-full object-cover"
                  />

                  {/* Category Badge */}
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
                    {SHARED_MEDIA_CATEGORY_LABELS[media.category]}
                  </div>

                  {/* Product Link Indicator */}
                  {media.product && (
                    <div className="absolute bottom-2 right-2">
                      <LinkIcon className="w-4 h-4 text-primary" />
                    </div>
                  )}

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-2 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyUrl(media.url);
                        }}
                        className="p-2 bg-white/90 dark:bg-slate-800/90 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors"
                        title="URL kopieren"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-700 dark:text-white" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMedia(media);
                        }}
                        className="p-2 bg-white/90 dark:bg-slate-800/90 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors"
                        title="Vorschau"
                      >
                        <Eye className="w-4 h-4 text-gray-700 dark:text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {selectedMedia && !onSelectMedia && (
        <SharedMediaPreviewModal
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}
    </div>
  );
}

function SharedMediaPreviewModal({
  media,
  onClose,
}: {
  media: SharedMedia;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(media.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error('Copy error:', error);
    }
  };

  const downloadImage = () => {
    const link = document.createElement('a');
    link.href = media.url;
    link.download = media.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate pr-2">
            {media.filename}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="aspect-video bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
            <img
              src={media.url}
              alt={media.alt_text || media.filename}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          <div className="p-4 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-slate-400 mb-1">Kategorie</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {SHARED_MEDIA_CATEGORY_LABELS[media.category]}
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-slate-400 mb-1">Dateigröße</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatFileSize(media.size)}
                </p>
              </div>
              {media.width && media.height && (
                <div>
                  <p className="text-gray-500 dark:text-slate-400 mb-1">Abmessungen</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {media.width} × {media.height} px
                  </p>
                </div>
              )}
              {media.product && (
                <div>
                  <p className="text-gray-500 dark:text-slate-400 mb-1">Produkt</p>
                  <p className="font-medium text-primary">{media.product.name}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-gray-500 dark:text-slate-400 mb-1 text-sm">URL</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  value={media.url}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-slate-300 min-w-0"
                />
                <button
                  onClick={copyUrl}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-white bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors flex-shrink-0"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Kopiert' : 'Kopieren'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-slate-700">
          <button
            onClick={downloadImage}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-white bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
